import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { WebsiteAuditService } from '../website-audit.service';
import { AddCompetitorComponent } from '../modals/add-competitor/add-competitor.component';

@Component({
  selector: 'app-audit-subbar',
  templateUrl: './audit-subbar.component.html',
  styleUrls: ['../audit-theme.css', './audit-subbar.component.css'],
})
export class AuditSubbarComponent implements OnInit, OnDestroy {
  private subs = new Subscription();

  public selectedTask: any;
  public competitors: any[] = [];
  public activeCompetitor: any = null;
  public periodMain = '';
  public periodCompare = '';
  // Shared read-only view — hide mutating actions like "Add competitor".
  public isShare = window.location.href.includes('/share/website-audit');

  constructor(
    private websiteAuditService: WebsiteAuditService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((task: any) => {
        this.selectedTask = task;
        // Only show competitors that are actively being audited AND finished
        // their first crawl (isUsable) as selectable chips. Disabled ones stay
        // available in the "Add competitor" modal; still-loading ones (isUsable
        // false) appear there as "Loading" until their data is ready.
        this.competitors = (task?.availableCompetitors || []).filter(
          (c: any) => c?.isActive !== false && c?.isUsable !== false,
        );
        this.activeCompetitor = task?.selectedCompetitor || null;
        this.buildPeriod(task);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get isBiweekly(): boolean {
    return (this.selectedTask?.auditFrequency || '') === 'biweekly';
  }

  private buildPeriod(task: any) {
    const dates: string[] = task?.overview?.scoreHistory?.dates || [];
    if (dates.length) {
      this.periodMain = this.fmtDate(dates[dates.length - 1]);
      this.periodCompare =
        dates.length > 1 ? `vs ${this.fmtDate(dates[dates.length - 2])}` : '';
    } else {
      const d = task?.updatedAt || task?.createdAt;
      this.periodMain = d ? this.fmtDate(d) : '';
      this.periodCompare = '';
    }
  }

  // ─── Competitor selection (broadcast so every tab re-renders) ───────────────

  isActiveCompetitor(comp: any): boolean {
    if (!this.activeCompetitor || !comp) return false;
    if (this.activeCompetitor.competitorId && comp.competitorId) {
      return this.activeCompetitor.competitorId === comp.competitorId;
    }
    return this.activeCompetitor.url === comp.url;
  }

  selectCompetitor(comp: any) {
    if (!comp || this.isActiveCompetitor(comp)) return;
    const url = comp?.url;
    if (!url) return;

    // Shared links are unauthenticated, so the authed task endpoint won't work —
    // fetch the competitor-scoped task through the public token endpoint instead.
    const request$ = this.isShare
      ? this.getShareToken()
        ? this.websiteAuditService.getAuditTaskFromToken(
            this.getShareToken(),
            url,
          )
        : null
      : this.selectedTask?.taskId
        ? this.websiteAuditService.getAuditTask(this.selectedTask.taskId, url)
        : null;

    if (!request$) return;

    // Show the switching overlay across every tab until the new data lands.
    this.websiteAuditService.setCompetitorSwitching(true);

    this.subs.add(
      request$.subscribe({
        next: (res: any) => {
          // Same audit, so this won't retrigger a full task reload — it just
          // swaps the competitor-specific overview/sections across all tabs.
          this.websiteAuditService.updateSelectedAuditTask({
            ...this.selectedTask,
            ...res,
          });
          this.websiteAuditService.setCompetitorSwitching(false);
        },
        error: () => {
          this.websiteAuditService.setCompetitorSwitching(false);
        },
      }),
    );
  }

  private getShareToken(): string {
    return (
      sessionStorage.getItem('shareableToken') ||
      sessionStorage.getItem('shareable-website-audit-auth') ||
      ''
    );
  }

  addCompetitor() {
    const parentTaskId = this.resolveParentTaskId();
    if (!parentTaskId) return;

    const competitors = this.selectedTask?.availableCompetitors || [];
    const ref = this.dialog.open(AddCompetitorComponent, {
      width: '430px',
      data: {
        taskId: parentTaskId,
        competitors,
        // Source list already is the benchmark set → all shown active.
        activeCompetitorIds: competitors
          .map((c: any) => c?.competitorId)
          .filter(Boolean),
      },
    });

    this.subs.add(
      ref.afterClosed().subscribe((result: any) => {
        if (!result) return;
        const taskId = this.selectedTask?.taskId;
        if (!taskId) return;
        this.websiteAuditService.getAuditTask(taskId).subscribe({
          next: (res: any) => {
            this.websiteAuditService.updateSelectedAuditTask({
              ...this.selectedTask,
              ...res,
            });
          },
        });
      }),
    );
  }

  // Competitors live on the biweekly parent task, not the run.
  private resolveParentTaskId(): string | null {
    const t = this.selectedTask;
    if (!t) return null;
    if (t.parentTaskId) return t.parentTaskId;
    const hist = Array.isArray(t.taskHistory) ? t.taskHistory : [];
    const root = hist.find((h: any) => h?.taskId && h.taskId === h?.parentTaskId);
    return root?.taskId || t.taskId || null;
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  getCompChipLabel(comp: any): string {
    return comp?.domain || this.getDomain(comp?.url || '');
  }

  getAuditFrequencyLabel(): string {
    const freq = this.selectedTask?.auditFrequency;
    if (!freq) return 'ONE-TIME';
    return freq === 'biweekly' ? 'BIWEEKLY' : freq.toUpperCase();
  }

  getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  private fmtDate(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }
}
